import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG = "https://images.unsplash.com/photo-1644375857059-e5b368156377?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtYW4lMjBmb3JtYWwlMjBzdWl0JTIwd2F0Y2glMjBsdXh1cnklMjBkYXJrJTIwYmFja2dyb3VuZCUyMHBvcnRyYWl0fGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc3NTg5MjMwNXww&ixlib=rb-4.1.0&q=85";

const CATEGORY_IMAGES = {
  fashion: "https://images.unsplash.com/photo-1614252368727-99517bc90d7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxzdHlsaXNoJTIwbWFuJTIwYXZpYXRvciUyMHN1bmdsYXNzZXMlMjBibGF6ZXIlMjBqYWNrZXQlMjBmYXNoaW9ufGVufDB8fHx8MTc3NTg5MjMwNXww&ixlib=rb-4.1.0&q=85",
  lifestyle: "https://images.unsplash.com/photo-1501555790667-ba7ea28b4cea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtaW5pbWFsaXN0JTIwd2F0Y2glMjBmbGF0bGF5fGVufDB8fHx8MTc3NTg5MDk4Nnww&ixlib=rb-4.1.0&q=85",
  "home-decor": "https://images.unsplash.com/photo-1638972691611-69633a3d3127?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob21lJTIwZGVjb3IlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzU4OTA5ODV8MA&ixlib=rb-4.1.0&q=85",
  other: "https://images.unsplash.com/photo-1583854327660-edc26bc594ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxzdHlsaXNoJTIwbWFuJTIwYXZpYXRvciUyMHN1bmdsYXNzZXMlMjBibGF6ZXIlMjBqYWNrZXQlMjBmYXNoaW9ufGVufDB8fHx8MTc3NTg5MjMwNXww&ixlib=rb-4.1.0&q=85",
};

const CATEGORY_LABELS = {
  fashion: 'Fashion',
  lifestyle: 'Lifestyle',
  'home-decor': 'Home Decor',
  other: 'Other',
};

export default function ArticleList() {
  const { category } = useParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = category ? `${API}/articles?category=${category}` : `${API}/articles`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        setArticles(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [category]);

  const label = CATEGORY_LABELS[category] || 'All Articles';
  const bgImage = CATEGORY_IMAGES[category] || HERO_IMG;

  return (
    <div className="min-h-screen pt-16" data-testid="article-list-page">
      {/* Category Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={bgImage} alt={label} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="relative z-10 text-center px-6">
          <p className="overline text-white/50 mb-4">Category</p>
          <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl font-black text-white tracking-tighter leading-none">
            {label}
          </h1>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-24">
              <div className="w-8 h-8 border-2 border-[#0A1128] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm text-[#6B7280] uppercase tracking-widest">Loading articles</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-[#6B7280] text-sm uppercase tracking-widest mb-4">No articles yet</p>
              <p className="text-[#0A1128] font-['Playfair_Display'] text-2xl">Check back soon for new content</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
              {articles.map((article, i) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className={`group opacity-0 animate-fade-in-up stagger-${(i % 5) + 1}`}
                  data-testid={`article-item-${article.slug}`}
                >
                  <div className="img-hover-zoom aspect-[4/3] mb-5">
                    <img
                      src={article.cover_image || CATEGORY_IMAGES[article.category] || HERO_IMG}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="overline text-[#6B7280] mb-2">{article.category?.replace('-', ' ')}</p>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0A1128] group-hover:text-[#6B7280] transition-colors leading-snug mb-3">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-2 text-[#0A1128] text-xs uppercase tracking-widest group-hover:gap-3 transition-all">
                    Read Article <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
